import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Spin, Empty, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import radarTilesService from '../../api/radarTilesService';
import circuitsService, { type CircuitWithGeoJSON } from '../../api/circuitsService';
import { MAPBOX_ACCESS_TOKEN } from '../../utils/constants';
import type { EventWithCircuit } from '../../api/eventsService';

dayjs.extend(utc);
dayjs.extend(timezone);

if (MAPBOX_ACCESS_TOKEN) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

interface MobileNowcastingViewProps {
  event: EventWithCircuit;
  theme: 'light' | 'dark';
}

function getRadarBboxCoordinates(event: EventWithCircuit): [[number, number], [number, number], [number, number], [number, number]] {
  if (event.radar_bbox?.type === 'Polygon' && event.radar_bbox.coordinates?.[0]) {
    const polygonCoords = event.radar_bbox.coordinates[0];
    let west = Infinity, east = -Infinity, south = Infinity, north = -Infinity;
    polygonCoords.forEach((coord: number[]) => {
      west = Math.min(west, coord[0]);
      east = Math.max(east, coord[0]);
      south = Math.min(south, coord[1]);
      north = Math.max(north, coord[1]);
    });
    return [
      [west, north],
      [east, north],
      [east, south],
      [west, south],
    ];
  }
  const lat = event.circuit?.latitude ?? 0;
  const lon = event.circuit?.longitude ?? 0;
  const offset = 0.45;
  return [
    [lon - offset, lat + offset],
    [lon + offset, lat + offset],
    [lon + offset, lat - offset],
    [lon - offset, lat - offset],
  ];
}

function useLatestRadarTile(event: EventWithCircuit | null) {
  const [tile, setTile] = useState<{ url: string; timestamp: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!event || event.observations_enabled === false) {
      setTile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const tz = event.circuit?.time_zone || 'UTC';
      const now = dayjs().tz(tz);
      const endUTC = now.utc();
      const startUTC = now.subtract(60, 'minute').utc();
      const startTimestamp = startUTC.format('YYYYMMDDHHmmss');
      const endTimestamp = endUTC.format('YYYYMMDDHHmmss');

      const data = await radarTilesService.getRadarTilesL2({
        start_timestamp: startTimestamp,
        end_timestamp: endTimestamp,
      });

      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const realtimeTiles = (data.tiles ?? []).filter((t) => !t.timestamp.includes('+'));
      if (realtimeTiles.length === 0) {
        setTile(null);
      } else {
        const latest = realtimeTiles[realtimeTiles.length - 1];
        let url = latest.url;
        if (url.startsWith('/api/')) {
          url = `${apiBase}${url}`;
        }
        setTile({ url, timestamp: latest.timestamp.split('+')[0] });
      }
    } catch (err) {
      console.error('Error fetching radar tiles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load radar');
      setTile(null);
    } finally {
      setLoading(false);
    }
  }, [event]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  return { tile, loading, error, refresh: fetchData };
}

function useCircuitData(event: EventWithCircuit | null) {
  const [circuitData, setCircuitData] = useState<CircuitWithGeoJSON | null>(null);

  useEffect(() => {
    if (!event) {
      setCircuitData(null);
      return;
    }
    let cancelled = false;
    circuitsService
      .getCircuitById(event.circuit_id, event.circuit_geojson_id)
      .then((data) => {
        if (!cancelled) setCircuitData(data);
      })
      .catch(() => {
        if (!cancelled) setCircuitData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [event, event?.circuit_id, event?.circuit_geojson_id]);

  return circuitData;
}

export const MobileNowcastingView: React.FC<MobileNowcastingViewProps> = ({ event, theme }) => {
  const { tile, loading, error, refresh } = useLatestRadarTile(event);
  const circuitData = useCircuitData(event);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const isDark = theme === 'dark';
  const tz = event.circuit?.time_zone || 'UTC';

  // Initialize map - delay so container has layout dimensions when tab becomes visible
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || !MAPBOX_ACCESS_TOKEN || !event) return;

    setMapReady(false);

    let ro: ResizeObserver | null = null;

    const timeoutId = setTimeout(() => {
      const el = mapContainerRef.current;
      if (!el) return;

      const circuitLat = event.circuit?.latitude ?? 0;
      const circuitLon = event.circuit?.longitude ?? 0;
      const styleUrl = isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11';

      const map = new mapboxgl.Map({
        container: el,
        style: styleUrl,
        center: [circuitLon, circuitLat],
        zoom: 11,
        minZoom: 6,
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-right');
      mapRef.current = map;

      map.on('load', () => {
        map.resize();
        setMapReady(true);
      });

      ro = new ResizeObserver(() => {
        if (mapRef.current) mapRef.current.resize();
      });
      ro.observe(el);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      ro?.disconnect();
      const map = mapRef.current;
      if (map) {
        map.remove();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, [event, isDark]);

  // Update radar layer when tile or map becomes ready
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !event || !mapReady) return;

    const sourceId = 'mobile-radar';
    const layerId = 'mobile-radar-layer';

    if (!tile) {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
      return;
    }

    const coords = getRadarBboxCoordinates(event);

    const applyLayer = () => {
      if (!map.isStyleLoaded()) {
        map.once('styledata', applyLayer);
        return;
      }

      if (map.getSource(sourceId)) {
        (map.getSource(sourceId) as mapboxgl.ImageSource).updateImage({ url: tile.url, coordinates: coords });
      } else {
        map.addSource(sourceId, {
          type: 'image',
          url: tile.url,
          coordinates: coords,
        });
        map.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: { 'raster-opacity': 0.8 },
        });
      }
    };

    applyLayer();

    return () => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(layerId)) m.removeLayer(layerId);
        if (m.getSource(sourceId)) m.removeSource(sourceId);
      } catch {
        // Map may already be destroyed when switching tabs
      }
    };
  }, [tile, event, mapReady]);

  // Draw circuit layer when circuitData and map are ready
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !event || !mapReady || !circuitData?.geojson) return;

    const circuitSourceId = 'mobile-circuit';
    const circuitLayerId = 'mobile-circuit-line';
    const stationsSourceId = 'mobile-circuit-stations';
    const stationsLayerId = 'mobile-circuit-stations';
    const stationsLabelsLayerId = 'mobile-circuit-stations-labels';

    const drawCircuit = () => {
      if (!map.isStyleLoaded()) {
        map.once('styledata', drawCircuit);
        return;
      }

      try {
        if (!map.getSource(circuitSourceId)) {
          map.addSource(circuitSourceId, {
            type: 'geojson',
            data: circuitData.geojson as GeoJSON.GeoJSON,
          });
          map.addLayer({
            id: circuitLayerId,
            type: 'line',
            source: circuitSourceId,
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
              'line-color': '#0E447D',
              'line-width': 3,
            },
          });
        }

        if (event.stations && event.stations.length > 0 && !map.getSource(stationsSourceId)) {
          const stationsGeoJSON: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: event.stations.map((station) => ({
              type: 'Feature' as const,
              geometry: {
                type: 'Point' as const,
                coordinates: [station.lon, station.lat],
              },
              properties: {
                reference: station.reference || 'N/A',
              },
            })),
          };
          map.addSource(stationsSourceId, {
            type: 'geojson',
            data: stationsGeoJSON,
          });
          map.addLayer({
            id: stationsLayerId,
            type: 'circle',
            source: stationsSourceId,
            paint: {
              'circle-radius': 8,
              'circle-color': '#FFA500',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            },
          });
          map.addLayer({
            id: stationsLabelsLayerId,
            type: 'symbol',
            source: stationsSourceId,
            layout: {
              'text-field': ['get', 'reference'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 11,
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#FFA500',
              'text-halo-width': 4,
              'text-halo-blur': 0,
            },
          });
        }
      } catch (e) {
        console.error('Error drawing circuit:', e);
      }
    };

    drawCircuit();

    return () => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(stationsLabelsLayerId)) m.removeLayer(stationsLabelsLayerId);
        if (m.getLayer(stationsLayerId)) m.removeLayer(stationsLayerId);
        if (m.getSource(stationsSourceId)) m.removeSource(stationsSourceId);
        if (m.getLayer(circuitLayerId)) m.removeLayer(circuitLayerId);
        if (m.getSource(circuitSourceId)) m.removeSource(circuitSourceId);
      } catch {
        // Map may already be destroyed when switching tabs
      }
    };
  }, [circuitData, event, mapReady]);

  if (event.observations_enabled === false) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>
        <Empty description="Radar is not available for this event" />
      </div>
    );
  }

  if (!MAPBOX_ACCESS_TOKEN) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)' }}>
        <Empty description="Map is not configured. Mapbox token is required." />
      </div>
    );
  }

  if (error && !tile) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)', marginBottom: 12 }}>{error}</p>
        <Button icon={<ReloadOutlined />} onClick={refresh}>Retry</Button>
      </div>
    );
  }

  const tileTime = tile
    ? dayjs.utc(tile.timestamp, 'YYYYMMDDHHmmss').tz(tz).format('HH:mm D MMM')
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 16 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)' }}>
          {tileTime ? `Latest radar ${tileTime}` : loading ? 'Loading radar…' : '—'}
        </span>
        <Button type="text" icon={<ReloadOutlined />} onClick={refresh} size="small">
          Refresh
        </Button>
      </div>

      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '50vh',
            minHeight: 300,
            borderRadius: 10,
            overflow: 'hidden',
            backgroundColor: isDark ? '#1f1f1f' : '#e5e5e5',
          }}
        />
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.5)',
              borderRadius: 10,
              gap: 12,
            }}
          >
            <Spin size="large" />
            <span style={{ color: 'rgba(255,255,255,0.9)' }}>Loading radar tiles…</span>
          </div>
        )}
        {!tile && !loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.4)',
              borderRadius: 10,
            }}
          >
            <Empty description="No radar data for the last hour" />
          </div>
        )}
      </div>
    </div>
  );
};
